import { onUnmounted, ref } from 'vue';

interface VerificationConfig {
  // Задержка после последнего изменения для проверки (мс)
  verificationDelay?: number;
  
  // Что отслеживать
  observeAttributes?: boolean;
  observeChildList?: boolean;
  observeSubtree?: boolean;
  
  // Включить логирование для отладки
  debug?: boolean;
}

/**
 * Composable для автоматической проверки состояния меню
 * при любых изменениях через MutationObserver.
 * 
 * Отслеживает изменения class и style в контейнере меню,
 * и запускает проверку состояния через заданную задержку
 * после последнего изменения (debounced verification).
 * 
 * Это решает проблемы рассинхронизации индикатора и цвета текста
 * на медленных устройствах или при быстрых изменениях активного пункта.
 * 
 * @example
 * ```typescript
 * const menuVerification = useMenuStateVerification({
 *   verificationDelay: 1000,
 *   debug: false
 * });
 * 
 * onMounted(() => {
 *   if (menuRef.value) {
 *     menuVerification.observe(menuRef.value, () => {
 *       // Проверить и исправить состояние
 *       verifyIndicatorState();
 *     });
 *   }
 * });
 * ```
 */
export function useMenuStateVerification(
  config: VerificationConfig = {}
) {
  const {
    verificationDelay = 1000,      // 1 секунда после последнего изменения
    observeAttributes = true,       // Отслеживать изменения атрибутов (class, style)
    observeChildList = false,       // Не нужно отслеживать добавление/удаление элементов
    observeSubtree = true,          // Отслеживать все вложенные элементы
    debug = false                   // Логирование для отладки
  } = config;

  const verifyTimer = ref<ReturnType<typeof setTimeout> | null>(null);
  let observer: MutationObserver | null = null;
  let verifyCallback: (() => void) | null = null;

  /**
   * Запустить/перезапустить таймер проверки
   */
  const scheduleVerification = () => {
    // Отменить предыдущий таймер
    if (verifyTimer.value !== null) {
      clearTimeout(verifyTimer.value);
      verifyTimer.value = null;
    }

    if (debug) {
      console.log('[MenuVerification] Scheduling verification in', verificationDelay, 'ms');
    }

    // Запланировать новую проверку
    verifyTimer.value = setTimeout(() => {
      if (debug) {
        console.log('[MenuVerification] Running verification callback');
      }
      
      if (verifyCallback) {
        verifyCallback();
      }
      verifyTimer.value = null;
    }, verificationDelay);
  };

  /**
   * Начать наблюдение за элементом
   */
  const observe = (element: HTMLElement, onVerify: () => void) => {
    // Сохранить callback
    verifyCallback = onVerify;

    // Создать observer
    observer = new MutationObserver((mutations) => {
      // Фильтруем только значимые изменения
      const hasSignificantChanges = mutations.some(mutation => {
        // Изменения class или style
        if (mutation.type === 'attributes') {
          const attrName = mutation.attributeName;
          const target = mutation.target as HTMLElement;
          
          // Игнорируем изменения в индикаторе (чтобы избежать зацикливания)
          // Индикатор имеет класс 'adv-menu__indicator'
          if (target.classList?.contains('adv-menu__indicator')) {
            if (debug) {
              console.log('[MenuVerification] Ignoring indicator change');
            }
            return false;
          }
          
          if (debug && (attrName === 'class' || attrName === 'style')) {
            console.log('[MenuVerification] Detected change:', {
              type: mutation.type,
              attribute: attrName,
              target: target.className || target
            });
          }
          
          return attrName === 'class' || attrName === 'style';
        }
        return false;
      });

      if (hasSignificantChanges) {
        // Перезапускаем таймер при каждом изменении
        scheduleVerification();
      }
    });

    // Начать наблюдение
    observer.observe(element, {
      attributes: observeAttributes,
      attributeFilter: ['class', 'style'], // Только class и style
      childList: observeChildList,
      subtree: observeSubtree
    });

    if (debug) {
      console.log('[MenuVerification] Started observing element');
    }

    // Запланировать первую проверку
    scheduleVerification();
  };

  /**
   * Принудительно запустить проверку сейчас
   */
  const verifyNow = () => {
    if (verifyTimer.value !== null) {
      clearTimeout(verifyTimer.value);
      verifyTimer.value = null;
    }
    
    if (debug) {
      console.log('[MenuVerification] Running immediate verification');
    }
    
    if (verifyCallback) {
      verifyCallback();
    }
  };

  /**
   * Cleanup
   */
  const cleanup = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
      
      if (debug) {
        console.log('[MenuVerification] Observer disconnected');
      }
    }
    
    if (verifyTimer.value !== null) {
      clearTimeout(verifyTimer.value);
      verifyTimer.value = null;
    }
    
    verifyCallback = null;
  };

  // Auto cleanup
  onUnmounted(cleanup);

  return {
    observe,
    verifyNow,
    cleanup
  };
}
